import { useEffect, useState } from "react";
import api, { formatApiError, showApiError } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { Apple, Sparkles, Heart, Check, X, Save, Plus, CheckCircle2, XCircle, Lock } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { PremiumLock, UpsellBanner, openUpgrade } from "@/components/PremiumLock";

const FREE_MAX_FAV = 3;
import AiLoader from "@/components/AiLoader";
import { toast } from "sonner";

const STATES = [
  { key: "keep", label: "Tieni", icon: Check, cls: "bg-primary text-primary-foreground border-primary" },
  { key: "favorite", label: "Preferito", icon: Heart, cls: "bg-accent text-accent-foreground border-accent" },
  { key: "remove", label: "Elimina", icon: X, cls: "bg-destructive text-destructive-foreground border-destructive" },
];

export default function FoodPreferences() {
  const { user } = useAuth();
  const premium = !!user?.is_premium;
  const [loading, setLoading] = useState(true);
  const [foods, setFoods] = useState([]);
  const [prefs, setPrefs] = useState({});
  const [generating, setGenerating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [newFood, setNewFood] = useState("");
  const [validating, setValidating] = useState(false);
  const [lastResult, setLastResult] = useState(null); // {approved, name, reason}

  const load = async () => {
    setLoading(true);
    const { data } = await api.get("/foods");
    setFoods(data.foods || []);
    setPrefs(data.preferences || {});
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const generate = async () => {
    setGenerating(true);
    try {
      const { data } = await api.post("/foods/generate");
      setFoods(data.foods || []);
      setPrefs(data.preferences || {});
      toast.success("Lista alimenti generata da DietFlix");
    } catch (err) {
      showApiError(err);
    } finally { setGenerating(false); }
  };

  const favCount = Object.values(prefs).filter((v) => v === "favorite").length;

  const choose = (name, key) => {
    if (!premium && key === "remove") { openUpgrade(); return; }
    if (!premium && key === "favorite" && prefs[name] !== "favorite" && favCount >= FREE_MAX_FAV) {
      toast.info(`Con il piano gratuito puoi scegliere ${FREE_MAX_FAV} preferiti. Passa a Premium per sceglierne quanti vuoi!`);
      openUpgrade();
      return;
    }
    setPrefs((p) => ({ ...p, [name]: key }));
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/foods/preferences", { preferences: prefs });
      toast.success("Preferenze salvate");
    } catch (err) {
      showApiError(err);
    } finally { setSaving(false); }
  };

  const validateFood = async () => {
    const name = newFood.trim();
    if (!name) { toast.error("Scrivi il nome di un alimento"); return; }
    setValidating(true);
    setLastResult(null);
    try {
      const { data } = await api.post("/foods/validate", { name });
      setLastResult(data);
      if (data.approved) {
        toast.success(`"${data.name}" approvato e aggiunto ai preferiti`);
        setNewFood("");
        await load();
      } else {
        toast.error(`"${name}" non approvato`);
      }
    } catch (err) {
      showApiError(err);
    } finally { setValidating(false); }
  };

  if (loading) return <div className="grid sm:grid-cols-2 gap-3">{[...Array(6)].map((_, i) => <Skeleton key={i} className="h-16 rounded-xl" />)}</div>;

  if (generating) return <Card className="rounded-2xl" data-testid="foods-generating"><CardContent className="p-0"><AiLoader type="foods" title="DietFlix sta scegliendo i tuoi alimenti" /></CardContent></Card>;

  const AddBoxInner = (
    <Card className="rounded-2xl border-dashed" data-testid="food-add-card">
      <CardContent className="p-4 space-y-3">
        <div className="flex items-center gap-2 text-sm font-medium"><Plus className="h-4 w-4 text-accent" /> Aggiungi un alimento a mano</div>
        <p className="text-xs text-muted-foreground">DietFlix valuterà se è compatibile con le tue esigenze prima di inserirlo.</p>
        <div className="flex gap-2">
          <Input value={newFood} onChange={(e) => setNewFood(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && !validating && validateFood()}
            placeholder="es. Salmone affumicato, Kefir…" data-testid="food-add-input" />
          <Button onClick={validateFood} disabled={validating} data-testid="food-add-button">
            <Sparkles className="h-4 w-4 mr-2" /> {validating ? "Valuto…" : "Valuta e aggiungi"}
          </Button>
        </div>
        {lastResult && (
          <div className={`rounded-xl border p-3 text-sm flex gap-2 ${lastResult.approved ? "bg-primary/5 border-primary/30" : "bg-destructive/5 border-destructive/30"}`}
            data-testid="food-validate-result">
            {lastResult.approved
              ? <CheckCircle2 className="h-5 w-5 text-primary shrink-0 mt-0.5" />
              : <XCircle className="h-5 w-5 text-destructive shrink-0 mt-0.5" />}
            <div>
              <div className="font-medium">{lastResult.approved ? "Approvato" : "Non approvato"}{lastResult.name ? ` · ${lastResult.name}` : ""}</div>
              <p className="text-muted-foreground">{lastResult.reason}</p>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );

  const AddBox = premium ? AddBoxInner : (
    <PremiumLock testid="foods-add-lock" title="Aggiungi i cibi che ami"
      description="Con Premium proponi qualsiasi alimento: DietFlix lo valuta e lo inserisce nella tua dieta.">
      {AddBoxInner}
    </PremiumLock>
  );

  if (!foods.length) {
    return (
      <div className="space-y-4">
        <Card className="rounded-2xl">
          <CardContent className="py-16 text-center space-y-4">
            <div className="h-14 w-14 rounded-2xl bg-secondary grid place-items-center mx-auto"><Apple className="h-7 w-7 text-primary" /></div>
            <h3 className="font-heading text-2xl font-semibold">Crea la tua lista di alimenti</h3>
            <p className="text-muted-foreground max-w-md mx-auto">DietFlix genererà una selezione di alimenti su misura per il tuo fabbisogno, patologie e stile alimentare.</p>
            <Button onClick={generate} disabled={generating} className="btn-brand border-0 rounded-full" data-testid="generate-foods-button">
              <Sparkles className="h-4 w-4 mr-2" /> {generating ? "Generazione in corso…" : "Genera lista alimenti"}
            </Button>
          </CardContent>
        </Card>
        {AddBox}
      </div>
    );
  }

  const categories = [...new Set(foods.map((f) => f.category))];

  return (
    <div className="space-y-6" data-testid="foods-section">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="font-heading text-2xl font-semibold">I tuoi alimenti</h2>
          <p className="text-muted-foreground text-sm">{premium ? "Scegli cosa tenere, cosa adori e cosa eliminare." : `Scegli fino a ${FREE_MAX_FAV} alimenti preferiti: DietFlix li userà nella tua dieta.`}</p>
        </div>
        <div className="flex gap-2 items-center">
          {!premium && (
            <span className={`rounded-full px-3 py-1 text-xs font-semibold font-mono-data ${favCount >= FREE_MAX_FAV ? "bg-accent text-accent-foreground" : "bg-secondary"}`} data-testid="free-fav-counter">
              <Heart className="h-3 w-3 inline mr-1" />{favCount}/{FREE_MAX_FAV} preferiti
            </span>
          )}
          <Button variant="outline" onClick={premium ? generate : openUpgrade} disabled={generating} data-testid="regenerate-foods-button">
            {premium ? <Sparkles className="h-4 w-4 mr-2" /> : <Lock className="h-4 w-4 mr-2" />} Rigenera
          </Button>
          <Button onClick={save} disabled={saving} data-testid="save-foods-button">
            <Save className="h-4 w-4 mr-2" /> {saving ? "Salvataggio…" : "Salva preferenze"}
          </Button>
        </div>
      </div>

      {!premium && (
        <UpsellBanner testid="foods-upsell" title="Preferiti illimitati ed esclusioni"
          text="Con Premium scegli tutti i preferiti che vuoi, escludi ciò che non ti piace e aggiungi qualsiasi alimento." />
      )}

      {AddBox}

      {categories.map((cat) => (
        <div key={cat}>
          <h3 className="font-heading text-lg font-semibold mb-3">{cat}</h3>
          <div className="grid sm:grid-cols-2 gap-3">
            {foods.filter((f) => f.category === cat).map((f) => (
              <div key={f.name} className="bg-card border rounded-xl p-4 flex items-center justify-between gap-3 card-lift" data-testid={`food-item-${f.name}`}>
                <div className="min-w-0">
                  <div className="font-medium truncate">{f.name}</div>
                  <div className="text-xs text-muted-foreground font-mono-data">{f.kcal_100g} kcal/100g{f.note ? ` · ${f.note}` : ""}</div>
                </div>
                <div className="flex gap-1 shrink-0">
                  {STATES.map(({ key, icon: Ic, cls, label }) => (
                    <button key={key} title={!premium && key === "remove" ? "Escludi (Premium)" : label} onClick={() => choose(f.name, key)}
                      className={`relative h-8 w-8 rounded-lg border grid place-items-center transition-colors ${prefs[f.name] === key ? cls : "bg-background text-muted-foreground hover:bg-secondary"} ${!premium && key === "remove" ? "opacity-60" : ""}`}
                      data-testid={`food-pref-${key}-${f.name}`}>
                      {!premium && key === "remove" ? <Lock className="h-3.5 w-3.5" /> : <Ic className="h-4 w-4" />}
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}
